class CartHeaderUpdater {
    constructor() {
        this.updateCartCountElements();
        this.fetchTimeout = null; // For debouncing
        this.init();
        this.setupMutationObserver();
    }

    updateCartCountElements() {
        // Re-query cart count elements to ensure we get all of them
        this.cartCountElements = document.querySelectorAll('[data-cart-count]');
    }

    init() {
        // Listen for cart updates
        document.addEventListener('cart:updated', (event) => {
            this.handleCartEvent(event);
        });

        // Listen for items added to cart
        document.addEventListener('cart:item-added', (event) => {
            this.handleCartEvent(event);
        });

        // Listen for cart changes (from cart drawer/popup)
        document.addEventListener('cart:changed', (event) => {
            this.handleCartEvent(event);
        });
    }

    handleCartEvent(event) {
        const cartFromEvent = this.getCartDataFromEvent(event);

        if (cartFromEvent) {
            this.updateCartCount(cartFromEvent);
        }

        // Debounced fetch to reconcile with Shopify's latest cart state
        this.debouncedFetch();
    }

    getCartDataFromEvent(event) {
        if (!event || !event.detail) return null;

        // Direct cart payload (cart.js response)
        if (event.detail.items || typeof event.detail.item_count === 'number') return event.detail;

        // Pub/Sub cart update payload
        if (event.detail.cartData && (event.detail.cartData.items || typeof event.detail.cartData.item_count === 'number')) {
            return event.detail.cartData;
        }

        // Some events nest payload under detail.detail
        if (
            event.detail.detail &&
            event.detail.detail.cartData &&
            (event.detail.detail.cartData.items || typeof event.detail.detail.cartData.item_count === 'number')
        ) {
            return event.detail.detail.cartData;
        }

        return null;
    }

    debouncedFetch() {
        // Clear any pending fetch
        if (this.fetchTimeout) {
            clearTimeout(this.fetchTimeout);
        }
        // Schedule a new fetch after 100ms of inactivity
        this.fetchTimeout = setTimeout(() => {
            this.fetchAndUpdateCart();
        }, 100);
    }

    setupMutationObserver() {
        if (this.cartObserver) return;

        const bubbleTarget = document.getElementById('cart-icon-bubble');
        const popupTarget = document.getElementById('cart-popup-panel');

        if (!bubbleTarget && !popupTarget) return;

        this.cartObserver = new MutationObserver(() => {
            this.debouncedFetch();
        });

        if (bubbleTarget) {
            this.cartObserver.observe(bubbleTarget, {
                childList: true,
                subtree: true,
                characterData: true,
            });
        }

        if (popupTarget) {
            this.cartObserver.observe(popupTarget, {
                childList: true,
                subtree: true,
                characterData: true,
            });
        }
    }

    async fetchAndUpdateCart() {
        try {
            // Short delay to ensure Shopify's cart API is updated
            await new Promise(resolve => setTimeout(resolve, 150));

            const response = await fetch(`/cart.js?ts=${Date.now()}`, { cache: 'no-store' });
            const cart = await response.json();

            // Calculate new count
            const newCount = cart.items ? cart.items.reduce((sum, item) => sum + item.quantity, 0) : 0;

            // Update the cart count display
            this.lastKnownCount = newCount;
            this.updateCartCount(cart);
        } catch (error) {
            // Cart fetch failed; header count left unchanged.
        }
    }

    updateCartCount(cart) {
        // Re-query elements to ensure we have all cart count elements
        this.updateCartCountElements();

        // Calculate total quantity of all items
        const totalQuantity = cart.items
            ? cart.items.reduce((sum, item) => sum + item.quantity, 0)
            : (typeof cart.item_count === 'number' ? cart.item_count : 0);

        this.cartCountElements.forEach(element => {
            // Check if this is a header menu cart count (desktop)
            if (element.classList.contains('cart-header-count')) {
                const nestedCartFont = element.querySelector('.cart-font');

                if (nestedCartFont) {
                    // Only replace the count so we keep the special font span intact
                    nestedCartFont.textContent = `(${totalQuantity})`;
                } else if (element.classList.contains('cart-font')) {
                    // Dropdown menu format: element itself is the cart font
                    element.textContent = `(${totalQuantity})`;
                } else {
                    // Check if this element's current text contains a word before the parentheses (mega-menu format)
                    // This works for "Cart (X)", "Panier (X)", or any translated text
                    const currentText = element.textContent.trim();
                    const hasTextBeforeParens = /^[^\(]+\s*\(/.test(currentText);

                    if (hasTextBeforeParens) {
                        // Extract the label part (e.g., "Cart" or "Panier") and keep it
                        const label = currentText.split('(')[0].trim();
                        element.textContent = `${label} (${totalQuantity})`;
                    } else {
                        // Fallback format: just (X)
                        element.textContent = `(${totalQuantity})`;
                    }
                }
                // Remove hidden class if present
                element.classList.remove('hidden');
            }
            // Check if this is the mobile cart count
            else if (element.classList.contains('cart-count-mobile')) {
                element.textContent = `(${totalQuantity})`;
                // Remove hidden class if present
                element.classList.remove('hidden');
            }
            else {
                element.textContent = totalQuantity;
                // Hide other cart counts when 0
                element.style.display = totalQuantity > 0 ? 'block' : 'none';
            }
        });

        // Also update any cart count badges
        const cartBadges = document.querySelectorAll('.cart-count-badge, [data-cart-item-count]');
        cartBadges.forEach(badge => {
            badge.textContent = totalQuantity;
            if (totalQuantity > 0) {
                badge.style.display = 'block';
                badge.classList.remove('hidden');
            } else {
                badge.style.display = 'none';
                badge.classList.add('hidden');
            }
        });
    }
}

// Initialize only once when DOM is loaded
let cartHeaderUpdaterInstance = null;

document.addEventListener('DOMContentLoaded', () => {
    if (!cartHeaderUpdaterInstance) {
        cartHeaderUpdaterInstance = new CartHeaderUpdater();
    }
});

// Also trigger update on page load to ensure correct count
window.addEventListener('load', () => {
    if (cartHeaderUpdaterInstance) {
        cartHeaderUpdaterInstance.fetchAndUpdateCart();
    } else {
        // Fallback in case DOMContentLoaded didn't fire yet
        cartHeaderUpdaterInstance = new CartHeaderUpdater();
        cartHeaderUpdaterInstance.fetchAndUpdateCart();
    }
});
